import { useRouter } from "next/router"
import { useEffect, useState } from "react"
import { useMediaPredicate } from "react-media-hook"
import { useIsStart } from "@/lib/helpers/template"
import { FormProvider, useForm } from "react-hook-form"
import { SearchControl } from "@/common/form/search-control/search-control"
import { SelectControl } from "@/common/form/select-control/select-control"
import Image from "next/legacy/image"
import Blocks from "@/components/Blocks"
import Breadcrumbs from "@/components/base/breadcrumbs/breadcrumbs"
import Article from "@/components/common/article/article"
import Block from "@/components/wrappers/block"
import useMenuItems from "@/lib/helpers/menu"
import styles from "./hero.module.css"
import classNames from "classnames"

export default function Hero({ block, settings }) {
  const [hasMounted, setMounted] = useState(false)
  const isMobile = useMediaPredicate("(max-width: 768px)")
  const hasTopMenu = !!useMenuItems("top-bramiljoval")
  const methods = useForm({
    defaultValues: { search: "", "marking-area": "" },
    mode: "onChange",
    shouldUnregister: true
  })

  // This is used due to isMobile not working before mount
  useEffect(() => {
    setMounted(true)
  }, [])

  const { innerBlocks } = block
  const {
    image,
    video,
    align,
    layout,
    size,
    opacity,
    mediaType,
    mediaAlign,
    panelSelect,
    creditTheme,
    creditEffect
  } = block.attrs

  let imageSrc = isMobile && image.tall ? image.tall : image.wide
  let videoSrc = isMobile && video.tall ? video.tall : video.wide
  let hasBackground = imageSrc || videoSrc
  let textColorClass = hasBackground ? "text-white" : "text-black-100"

  const baseBackground = mediaType === "video" ? "" : "bg-good-green-hero"
  const hasArticles = block.attrs.articles.length > 0
  const blockGap = hasArticles ? "normal" : block.attrs.blockGap

  const classes = classNames(
    styles.hero,
    layout,
    baseBackground,
    textColorClass,
    size,
    {
      "has-articles": hasArticles,
      "no-panel": panelSelect === "hide",
      "search-panel": panelSelect === "search",
      "is-centered": align === "center",
      "has-top-menu": hasTopMenu
    }
  )

  return (
    <Block block={block} className={classes} width="bleed" gap={blockGap}>
      {hasMounted &&
        mediaType === "image" &&
        imageSrc &&
        layout !== "bra-miljoval" && (
          <figure className="background">
            <Image
              className="object-cover object-center w-full"
              src={imageSrc.url}
              alt={block?.attrs?.image?.wide?.alt}
              layout="fill"
              priority={true}
              placeholder="empty"
              loading="eager"
            />
          </figure>
        )}
      {hasMounted && mediaType === "video" && (
        <div className={classNames(styles.background, "items-" + mediaAlign)}>
          <video
            className={styles.videoOverlay}
            preload="auto"
            autoPlay={true}
            loop={true}
            muted={true}
            playsInline={true}>
            <source src={videoSrc} type="video/mp4" />
          </video>
        </div>
      )}
      {hasBackground && (
        <div className={classNames(styles.overlay, "opacity" + opacity)} />
      )}
      <Breadcrumbs
        hidden={useIsStart() || settings?.hideBreadcrumbs}
        wrapperClassName="hero-breadcrumbs"
      />
      <div className="inner-wrapper">
        <div className="content">
          <Blocks blocks={innerBlocks} settings={settings} />
          <Credit
            {...{
              hasMounted,
              innerBlocks,
              image: imageSrc,
              theme: creditTheme,
              effect: creditEffect
            }}
          />
        </div>
        {hasMounted && block.attrs.image.url && layout === "bra-miljoval" && (
          <figure className="hero-image">
            <Image
              className="object-cover object-center w-full"
              src={block.attrs.image.url}
              alt={block?.attrs?.image?.alt}
              layout="fill"
              priority={true}
              placeholder="empty"
              loading="eager"
            />
            <figcaption>
              <Credit
                {...{
                  hasMounted,
                  innerBlocks,
                  image: block.attrs.image,
                  theme: creditTheme,
                  effect: creditEffect
                }}
              />
            </figcaption>
          </figure>
        )}
      </div>
      <Articles attrs={block.attrs} textColorClass={textColorClass} />
      {layout === "bra-miljoval" && panelSelect === "search" && (
        <FormProvider {...methods}>
          <SearchPanel
            {...{ search: block.attrs?.search, methods, textColorClass }}
          />
        </FormProvider>
      )}
    </Block>
  )
}

export function Credit({
  hasMounted,
  innerBlocks,
  image,
  theme = "",
  effect = ""
}) {
  if (!hasMounted) {
    return null
  }

  if (!image?.credit || image.credit.length === 0) {
    return null
  }

  const isSerif = [...innerBlocks]
    .find(
      (block) =>
        block?.name === "core/paragraph" &&
        block?.attrs?.classList &&
        !block?.attrs?.classList.includes("object-tag")
    )
    ?.attrs?.classList?.includes("serif")

  return (
    <div
      className={classNames([
        "credit text-effect",
        { "has-bg": effect.endsWith("-bg"), serif: isSerif },
        theme.replace("has-", "").replace("-color", ""),
        effect.replace("has-", "")
      ])}>
      {image.type}: {image.credit}
    </div>
  )
}

const Articles = ({ attrs, textColorClass }) => {
  const { articles, articlesHeading } = attrs

  if (attrs.layout === "bra-miljoval" && attrs.panelSelect !== "articles") {
    return
  }

  if (articles.length === 0) {
    return null
  }

  return (
    <div className="panel-wrapper">
      <div className="articles-wrapper">
        {articlesHeading && (
          <h2 className={classNames([textColorClass])}>{articlesHeading}</h2>
        )}
        <div className={styles.articles}>
          {articles.map((article, index) => (
            <Article
              key={`article-${index}`}
              className={styles.article}
              item={article}
              showTag={false}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

const SearchPanel = ({ search, methods }) => {
  const { handleSubmit, control, resetField, getValues, watch, setValue } =
    methods
  const [openFilter, setOpenFilter] = useState(null)
  const router = useRouter()

  const markingArea = search?.search?.filters.find(
    (item) => item.taxonomy === "marking-area"
  )
  const { taxonomy, label, options } = markingArea ?? search

  const onSubmit = (_, e) => {
    e.preventDefault()
    const values = getValues()
    const query = {}

    if (values.search) {
      query.s = values.search
    }

    if (taxonomy && values[taxonomy]) {
      query[taxonomy] = values[taxonomy]
    }

    router.push({
      pathname: search?.search?.path ?? "/produkter/",
      query: query
    })
  }

  const onSelect = (name, value) => {
    setValue(name, value)
    setOpenFilter(null)
  }

  return (
    <div className="panel-wrapper">
      <div className="search-wrapper">
        <h3>{search?.heading}</h3>
        <p>{search?.text}</p>
        <form onSubmit={handleSubmit(onSubmit)}>
          <SearchControl
            {...{ control }}
            isLoading={false}
            name="search"
            className="search"
            onReset={() => resetField("search")}
            placeholder={search?.search?.placeholder ?? "Sök..."}
            onSubmit={(e) => onSubmit(null, e)}
          />
          {taxonomy && (
            <SelectControl
              key={taxonomy}
              className="marking-area"
              isOpen={openFilter === taxonomy}
              {...{
                control,
                label,
                name: taxonomy
              }}
              choices={options}
              onSelect={(name, value) => onSelect(name, value)}
              selected={watch(taxonomy)}
              setOpen={() =>
                setOpenFilter(openFilter === taxonomy ? null : taxonomy)
              }
            />
          )}
        </form>
      </div>
    </div>
  )
}
